/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useBrandingSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling branding form submission.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { useAuthStore } from '../../../store/auth';
import { updateBranding } from '../api/brandingApi';
import type { BrandingConfig } from '../types';

export function useBrandingSubmit(
  tenantId: number | null,
  formData: BrandingConfig,
  onSuccess: (config: BrandingConfig) => void
) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || 'main';

  const [saving, setSaving] = useState(false);

  const handleSubmit = useCallback(
    async (hasChanges: boolean) => {
      if (!tenantId) {
        showError(t('branding.errors.noTenant'));
        return;
      }

      // hasChanges validation removed

      setSaving(true);
      try {
        const result = await updateBranding(
          tenantId,
          formData,
          tenantSlug,
          user?.id ? Number(user.id) : undefined
        );

        onSuccess(result.brandingConfig);
        showSuccess(t('branding.updateSuccess'));
      } catch (error) {
        showError(t('branding.updateError'));
      } finally {
        setSaving(false);
      }
    },
    [
      tenantId,
      formData,
      tenantSlug,
      user?.id,
      onSuccess,
      showError,
      showInfo,
      showSuccess,
      t,
    ]
  );

  return {
    saving,
    handleSubmit,
  };
}

